import { useCallback, useMemo } from 'react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type { DragStart, DropResult, DragUpdate } from 'react-beautiful-dnd';
import type { Transition } from '@atlassian/jira-business-board-workflow-issues';
import { useCollabStore, getPresenceNodeId } from '@atlassian/jira-business-collaboration';
import {
	GROUP_BY_ASSIGNEE,
	GROUP_BY_CATEGORY,
	GROUP_BY_PRIORITY,
	GROUP_BY_STATUS,
} from '@atlassian/jira-business-constants';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking';
import { useOpenIssueTransitionModal } from '@atlassian/jira-issue-transition-trigger';
import { createAri } from '@atlassian/jira-platform-ari';
import type { CardId } from '@atlassian/jira-platform-board-kit';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { toIssueId } from '@atlassian/jira-shared-types';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import {
	InvalidMove,
	TRANSITION_ISSUE_EXPERIENCE,
	RANK_ISSUE_EXPERIENCE,
	CARD_DND_TYPE,
} from '../../common/constants';
import type { ColumnId } from '../../common/types';
import {
	useBoardStore,
	useColumnByStatusId,
	useGetIssueById,
	useHasMultipleTransitionsBetweenStatuses,
	useTransitionById,
	useTransitionsByStatus,
} from '../../services/board-store';
import { useRankIssue } from '../../services/rank-issue';
import { useTransitionAndRank } from '../../services/transition-and-rank-old';
import type { TransitionAndRankInput } from '../../services/transition-and-rank-old/types';
import { useCardAnimation } from '../card-animation-old';
import { useCardGroupMove } from '../card-group-move-old';
import { useColumns } from '../columns-old';
import { useGroupBy } from '../group-by';
import {
	transitionAndRankAction,
	transitionAction,
	rankAction,
	JWM_BOARD_CARD_ISSUE_TRANSITION,
} from './constants';
import {
	getColumnById,
	getDefaultTransition,
	getOptimisticUpdatesForAssignee,
	getOptimisticUpdatesForCategory,
	getOptimisticUpdatesForPriority,
	getRankTarget,
	isInvalidMove,
	isValidationOrNetworkError,
} from './utils';

const emptyArray: never[] = [];

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useCardTransitionsOld = (statusId?: ColumnId) => {
	const [
		{ issues, cardDragState, transitions: allTransitions },
		{ setIssues, setCardDragState, moveIssues, updateIssue, pushToJobQueue },
	] = useBoardStore();
	const [getIssueById] = useGetIssueById();
	const [transitions] = useTransitionsByStatus({
		fromStatusId: Number(cardDragState.issue?.statusId ?? 0),
		toStatusId: Number(statusId ?? 0),
	});
	const [transition] = useTransitionById({ transitionId: cardDragState.transitionId });
	const [sourceColumn] = useColumnByStatusId({ statusId: Number(cardDragState.issue?.statusId) });
	const { loading: issueRankInProgress, rankIssueAfter, rankIssueBefore } = useRankIssue();
	const [hasMultipleTransitions] = useHasMultipleTransitionsBetweenStatuses();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const startTransitionExperience = useExperienceStart(TRANSITION_ISSUE_EXPERIENCE);
	const markTransitionExperienceSuccess = useExperienceSuccess(TRANSITION_ISSUE_EXPERIENCE);
	const markTransitionExperienceFailed = useExperienceFail(TRANSITION_ISSUE_EXPERIENCE);

	const startRankExperience = useExperienceStart(RANK_ISSUE_EXPERIENCE);
	const markRankExperienceSuccess = useExperienceSuccess(RANK_ISSUE_EXPERIENCE);
	const markRankExperienceFailed = useExperienceFail(RANK_ISSUE_EXPERIENCE);

	const project = useProject();
	const projectId = String(project.id);
	const hasScheduleIssuesPermission = project.permissions.scheduleIssues;
	const { onAnimationStart, onAnimationEnd } = useCardAnimation();
	const [{ dragAnimation }, { stopDragAnimation }] = useCollabStore();

	const { transitionAndRank } = useTransitionAndRank();

	const [groupBy] = useGroupBy();
	const isGroupedByStatus = groupBy === GROUP_BY_STATUS;

	const boardColumns = useColumns();
	const cardGroupMove = useCardGroupMove();

	const targetColumn = useMemo(
		() => getColumnById(cardDragState.columnId, boardColumns),
		[boardColumns, cardDragState.columnId],
	);

	const isTargetColumnCollapsed = useMemo(
		() => targetColumn != null && !!targetColumn.isCollapsed,
		[targetColumn],
	);

	const openIssueTransitionModal = useOpenIssueTransitionModal();
	const cloudId = useCloudId();

	// For the given arguments, executes rank and transition sequentially
	const getTransitionAndRankPromise = (
		issueId: number,
		getRankPromise: () => Promise<void>,
		defaultTransition: Transition,
		shouldRankAfter: boolean,
		relativeIssueId: null | number,
	) =>
		new Promise(
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(resolve: (result: Promise<void> | void) => void, reject: (error?: any) => void) => {
				const processRankPromise = () => getRankPromise().then(resolve).catch(reject);
				const transitionToExecute = hasMultipleTransitions ? transition : defaultTransition;
				const { transitionId, hasScreen } = transitionToExecute ?? {};
				const analyticsEventData = { hasScreen, issueId };
				const analyticsSuccess = () => {
					markTransitionExperienceSuccess();
					markRankExperienceSuccess();
					fireUIAnalytics(
						createAnalyticsEvent({}),
						`${transitionAndRankAction} success`,
						analyticsEventData,
					);
					fireTrackAnalytics(createAnalyticsEvent({}), 'issueTransitionAndRank success', {
						triggerPointKey: JWM_BOARD_CARD_ISSUE_TRANSITION,
						isModalOpen: false,
					});
				};
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const analyticsFailure = (err: any) => {
					// Exclude client fetch, validation and 4xx errors from experience fail events. Error message for
					// transition and rank is displayed at board level.
					if (isValidationOrNetworkError(err)) {
						markRankExperienceSuccess();
						markTransitionExperienceSuccess();
					} else {
						markTransitionExperienceFailed('Issue transition and rank failed.', err);
						markRankExperienceFailed('Issue transition and rank failed.', err);
						fireUIAnalytics(
							createAnalyticsEvent({}),
							`${transitionAndRankAction} failure`,
							analyticsEventData,
						);
						fireTrackAnalytics(createAnalyticsEvent({}), 'issueTransitionAndRank failed', {
							triggerPointKey: JWM_BOARD_CARD_ISSUE_TRANSITION,
							isModalOpen: false,
						});
					}
					throw err;
				};
				if (hasScreen) {
					const handleDialogSuccess = () => {
						processRankPromise();
						markTransitionExperienceSuccess();
						fireUIAnalytics(
							createAnalyticsEvent({}),
							`${transitionAction} success`,
							analyticsEventData,
						);
						fireTrackAnalytics(createAnalyticsEvent({}), 'issueTransition success', {
							triggerPointKey: JWM_BOARD_CARD_ISSUE_TRANSITION,
							isModalOpen: true,
						});
					};
					const handleDialogError = () => {
						markTransitionExperienceFailed(
							'Issue transition failed.',
							new Error('Issue transition with screen failed.'),
						);
						fireUIAnalytics(
							createAnalyticsEvent({}),
							`${transitionAction} failure`,
							analyticsEventData,
						);
						fireTrackAnalytics(createAnalyticsEvent({}), 'issueTransition failed', {
							triggerPointKey: JWM_BOARD_CARD_ISSUE_TRANSITION,
							isModalOpen: true,
						});
						reject();
					};

					const handleDialogCancel = () => {
						fireTrackAnalytics(createAnalyticsEvent({}), 'issueTransition cancel', {
							triggerPointKey: JWM_BOARD_CARD_ISSUE_TRANSITION,
							isModalOpen: true,
						});
						reject();
					};
					const issueKey = getIssueById(issueId)?.key;
					const formattedIssueId = toIssueId(String(issueId));

					const ariIssueId = createAri({
						resourceOwner: 'jira',
						cloudId,
						resourceType: 'issue',
						resourceId: formattedIssueId ?? '',
					});

					startTransitionExperience();

					openIssueTransitionModal?.({
						payload: {
							issueId: ariIssueId,
							issueKey,
							// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
							transitionId: String(transitionId!),
						},
						triggerPointKey: 'issue-transition-board-card-transition',
						onDialogSuccess: handleDialogSuccess,
						onDialogCancel: handleDialogCancel,
						onDialogError: handleDialogError,
					});
				} else {
					startTransitionExperience();
					startRankExperience();

					const transitionAndRankInput: TransitionAndRankInput = {
						issueIds: [issueId],
						transitionId: transitionId != null ? Number(transitionId) : undefined,
					};

					if (hasScheduleIssuesPermission) {
						transitionAndRankInput.afterIssueId =
							relativeIssueId != null && shouldRankAfter ? relativeIssueId : undefined;

						transitionAndRankInput.beforeIssueId =
							relativeIssueId != null && !shouldRankAfter ? relativeIssueId : undefined;
					}

					transitionAndRank(transitionAndRankInput)
						.then(resolve)
						.then(analyticsSuccess)
						.catch(analyticsFailure)
						.catch(reject);
				}
			},
		);

	const onDragStart = (dragStart?: DragStart) => {
		if (dragStart === undefined) {
			return;
		}
		const { draggableId, type } = dragStart;
		if (type === CARD_DND_TYPE) {
			const issueId = draggableId.split('ISSUE::')[1];
			setCardDragState({ ...cardDragState, issue: getIssueById(Number(issueId)) });

			const nodeId = getPresenceNodeId({
				projectId,
				viewId: 'board',
				nodeId: issueId,
			});

			// set telepointer of animated card for current user
			onAnimationStart({ issueId: Number(issueId), nodeId });
		}
	};
	const onDragEnd = (dragStop?: DropResult) => {
		if (!dragStop?.destination && dragStop?.reason === 'DROP') {
			// if the drag destination is null is because the Droppable component is disabled
			// so the user is trying to do an invalid move
			fireTrackAnalytics(createAnalyticsEvent({}), 'cardTransition invalidMove', {
				isTargetColumnCollapsed,
				hasMultipleTransitions,
			});
		}
		setCardDragState({
			...cardDragState,
			issue: null,
			hoverIntent: false,
			transitionId: null,
			columnId: null,
		});

		if (dragStop?.draggableId) {
			const issueId = dragStop.draggableId.split('ISSUE::')[1];
			const nodeId = getPresenceNodeId({
				projectId,
				viewId: 'board',
				nodeId: issueId,
			});

			onAnimationEnd({ issueId: Number(issueId), nodeId, dragStop, issues });
		}
	};

	const onCardDragOver = useCallback(
		(columnId: ColumnId) => (transitionId: number | null, hoverIntent: boolean) => {
			setCardDragState({
				...cardDragState,
				transitionId,
				columnId,
				issue: cardDragState.issue,
				hoverIntent,
				...(transitionId !== null && {
					hoverIntent: cardDragState.hoverIntent || (hoverIntent && !!transitionId),
				}),
			});
		},
		[cardDragState, setCardDragState],
	);

	const onDragUpdate = useCallback(
		(dragUpdated?: DragUpdate) => {
			if (dragUpdated == null) return;

			const { destination, type, draggableId } = dragUpdated;
			const issueId = draggableId.split('ISSUE::')[1];
			if (destination && type === CARD_DND_TYPE) {
				const columnId = destination.droppableId.split('COLUMN::')[1];
				const maybeNumberColumnID = Number(columnId);
				setCardDragState({
					...cardDragState,
					issue: getIssueById(Number(issueId)),
					columnId: Number.isNaN(maybeNumberColumnID) ? columnId : maybeNumberColumnID,
				});
			}
		},
		[cardDragState, setCardDragState, getIssueById],
	);

	const rollback = () => setIssues([...issues]);

	const moveItems = (
		issueIDs: number[],
		sourceColumnId: ColumnId,
		targetColumnId: ColumnId,
		sourceIndex: number,
		destinationIndex: number,
	): Promise<void> => {
		if (
			isInvalidMove({
				sourceColumnId,
				targetColumnId,
				sourceIndex,
				destinationIndex,
				transition,
				hasMultipleTransitions,
				isGroupedByStatus,
				issueRankInProgress,
				isTargetColumnCollapsed,
			})
		) {
			fireTrackAnalytics(createAnalyticsEvent({}), 'cardTransition invalidMove', {
				isTargetColumnCollapsed,
				hasMultipleTransitions,
			});
			return Promise.reject(new InvalidMove());
		}
		const targetColumnIssues = targetColumn?.issues ?? emptyArray;

		const { shouldRankAfter, relativeIssueId } = getRankTarget({
			targetColumn,
			sourceColumnId,
			targetColumnId,
			sourceIndex,
			destinationIndex,
			isTargetColumnCollapsed,
		});

		if (targetColumn && targetColumn.groupBy === GROUP_BY_PRIORITY) {
			moveIssues(
				issueIDs,
				sourceColumnId,
				targetColumnId,
				relativeIssueId,
				shouldRankAfter,
				getOptimisticUpdatesForPriority(targetColumn),
			);
		} else if (targetColumn && targetColumn.groupBy === GROUP_BY_ASSIGNEE) {
			moveIssues(
				issueIDs,
				sourceColumnId,
				targetColumnId,
				relativeIssueId,
				shouldRankAfter,
				getOptimisticUpdatesForAssignee(targetColumn),
			);
		} else if (targetColumn && targetColumn.groupBy === GROUP_BY_CATEGORY) {
			moveIssues(
				issueIDs,
				sourceColumnId,
				targetColumnId,
				relativeIssueId,
				shouldRankAfter,
				getOptimisticUpdatesForCategory(targetColumn),
			);
		} else if (relativeIssueId !== null || targetColumnId !== sourceColumnId) {
			// Optimistic move issue
			moveIssues(issueIDs, sourceColumnId, targetColumnId, relativeIssueId, shouldRankAfter);
		}

		// we only need to move animated issue optimistically, because it's already been updated on BE by a remote user
		// after the card is droppped, we refetch board data to make sure there's no stale state
		const animatedIssueId = dragAnimation
			.map((a) => a.sourceNodeId)
			.find((issueId) => issueIDs.includes(issueId));

		if (animatedIssueId != null) {
			stopDragAnimation({ sourceNodeId: `${animatedIssueId}` });
			return Promise.resolve();
		}

		const issueIdsToProcess = shouldRankAfter ? [...issueIDs].reverse() : [...issueIDs];

		const rankAnalytics = (action: string) =>
			fireUIAnalytics(createAnalyticsEvent({}), action, {
				issuesInColumnCount: targetColumnIssues.length,
			});
		const getRankPromise = (issueId: number) => {
			if (!hasScheduleIssuesPermission || relativeIssueId == null || relativeIssueId === issueId) {
				return () => Promise.resolve();
			}
			return () => {
				startRankExperience();
				const onRankSuccess = () => {
					markRankExperienceSuccess();
					rankAnalytics(`${rankAction} success`);
				};
				const onRankFail = (err: Error) => {
					if (isValidationOrNetworkError(err)) {
						markRankExperienceSuccess();
						rankAnalytics(`${rankAction} success`);
					} else {
						markRankExperienceFailed('Issue rank failed.', err);
						rankAnalytics(`${rankAction} failure`);
					}

					throw err;
				};
				return shouldRankAfter
					? rankIssueAfter({ issueId, relativeIssueId }).then(onRankSuccess).catch(onRankFail)
					: rankIssueBefore({ issueId, relativeIssueId }).then(onRankSuccess).catch(onRankFail);
			};
		};

		if (!isGroupedByStatus) {
			return cardGroupMove({
				issueIds: issueIdsToProcess,
				// @ts-expect-error - Type 'null' is not assignable to type 'BoardColumn'
				targetColumn,
				getRankPromise,
				rollback,
			});
		}
		return pushToJobQueue(() =>
			issueIdsToProcess
				.map(
					(issueId) => () =>
						getTransitionAndRankPromise(
							issueId,
							getRankPromise(issueId),
							getDefaultTransition(
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								sourceColumnId as number,
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								targetColumnId as number,
								allTransitions,
							),
							shouldRankAfter,
							relativeIssueId,
						),
				)
				.reduce(
					(previousPromise, currentTransitionAndRankPromise) =>
						previousPromise.then(currentTransitionAndRankPromise).catch((err) => {
							rollback();
							throw err;
						}),
					Promise.resolve(),
				)
				.then(() => {
					issueIdsToProcess.forEach((issueId) =>
						// TODO JET-1952
						updateIssue(issueId, {
							// @ts-expect-error - Type 'string' is not assignable to type 'number'.
							statusId: targetColumnId,
						}),
					);
					return Promise.resolve();
				}),
		);
	};

	const onCardsMove = (
		cardIds: CardId[],
		sourceColumnId: ColumnId,
		destinationColumnId: ColumnId,
		sourceCardIndex: number,
		destinationCardIndex: number,
	) =>
		moveItems(
			/**
			 * The string cardId type is only used by Advanced Roadmaps increment planning board.
			 * In JWM the cardId should be expected to be a number.
			 */
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			cardIds as number[],
			sourceColumnId,
			destinationColumnId,
			sourceCardIndex,
			destinationCardIndex,
		);

	return {
		transitions,
		transition,
		sourceColumn,
		cardDragState,
		onDragStart,
		onDragEnd,
		onDragUpdate,
		onCardDragOver,
		onCardsMove,
	};
};
